// Client HTTP signé du core THOR — port navigateur de thor/core_client/client.py.
// Toutes les requêtes (sauf l'enrôlement) sont signées Ed25519. Toute erreur
// réseau renvoie null → l'appelant bascule sur le moteur local hors-ligne.

import { CORE_ENABLED, CORE_URL } from "../config";
import type { BoardModel, Cause, Domaine } from "../domain/types";
import { Identity } from "./identity";

const TIMEOUT_MS = 8000;

export interface CoreFaultOut {
  id: string;
  domaine: Domaine;
  device_brand?: string | null;
  device_model?: string | null;
  symptom: string;
  probable_causes: Cause[];
  source: string;
  updated_at: string;
}

export interface DiagnoseResponse {
  causes: Cause[];
  source: string; // "rules" | "rules+ai"
  fault_id?: string | null;
}

export interface CoreBoardOut {
  id: string;
  payload: BoardModel;
  updated_at: string;
}

export interface SyncResponse {
  accepted: number;
  faults: CoreFaultOut[];
  boards?: CoreBoardOut[]; // optionnel : core qui ne renvoie pas encore les boards
  server_time: string;
}

function withTimeout(ms: number): { signal: AbortSignal; cancel: () => void } {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return { signal: ctrl.signal, cancel: () => clearTimeout(t) };
}

export class CoreClient {
  private constructor(readonly identity: Identity) {}

  static async create(): Promise<CoreClient | null> {
    if (!CORE_ENABLED) return null;
    return new CoreClient(await Identity.loadOrCreate());
  }

  private async postSigned<T>(path: string, payload: unknown): Promise<T | null> {
    if (!CORE_ENABLED || !this.identity.clientId) return null;
    const body = new TextEncoder().encode(JSON.stringify(payload));
    const ts = String(Math.floor(Date.now() / 1000));
    const { signal, cancel } = withTimeout(TIMEOUT_MS);
    try {
      const r = await fetch(CORE_URL + path, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          "X-Client-Id": this.identity.clientId,
          "X-Timestamp": ts,
          "X-Signature": this.identity.sign(ts, body),
        },
        body,
      });
      if (!r.ok) return null;
      return (await r.json()) as T;
    } catch {
      return null; // hors-ligne / timeout → fallback local
    } finally {
      cancel();
    }
  }

  /** Enrôlement (non signé : clé publique + auto-signature). Retourne le client_id. */
  async enroll(produit: string, tier: string, fingerprint: string, license: string | null): Promise<string | null> {
    if (!CORE_ENABLED) return null;
    const payload = {
      public_key: this.identity.publicKeyHex,
      produit,
      tier,
      fingerprint,
      self_signature: this.identity.selfSignature(),
      license: license ?? "",
    };
    const { signal, cancel } = withTimeout(TIMEOUT_MS);
    try {
      const r = await fetch(CORE_URL + "/v1/enroll", {
        method: "POST",
        signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) return null;
      const data = (await r.json()) as { client_id: string };
      await this.identity.setClientId(data.client_id);
      return data.client_id;
    } catch {
      return null;
    } finally {
      cancel();
    }
  }

  diagnose(req: {
    domaine: Domaine;
    symptom: string;
    device_brand?: string | null;
    device_model?: string | null;
  }): Promise<DiagnoseResponse | null> {
    return this.postSigned<DiagnoseResponse>("/v1/diagnose", req);
  }

  sync(
    events: unknown[],
    faultsSince: string | null,
    boardsSince: string | null = null,
  ): Promise<SyncResponse | null> {
    return this.postSigned<SyncResponse>("/v1/sync", {
      events,
      faults_since: faultsSince,
      boards_since: boardsSince,
    });
  }
}
